import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { type PortCongestion, fetchPortCongestion } from '../lib/ports-api'
import { type StormExposure, fetchLaneExposure, intensityStyle } from '../lib/weather-api'

// "Current disruptions on this lane" — fuses port-congestion at the POL + POD with the lane's
// storm exposure into one module on the lane page. Credibility = conversion: every figure cites its
// source + hourly freshness, the clear state is honest (not hidden), and it always ends in an
// actionable next step (compare the least-affected carriers, or open the affected port).

const LEVEL = {
  severe: { color: 'var(--danger)', label: 'Severe congestion' },
  moderate: { color: 'var(--warn)', label: 'Moderate congestion' },
  mild: { color: 'var(--signal)', label: 'Mild congestion' },
} as const

interface Props {
  pol: string
  pod: string
  polName: string
  podName: string
}

export function LaneDisruption({ pol, pod, polName, podName }: Props) {
  const [polCong, setPolCong] = useState<PortCongestion | null>(null)
  const [podCong, setPodCong] = useState<PortCongestion | null>(null)
  const [storms, setStorms] = useState<StormExposure[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let live = true
    if (!pol || !pod) return
    Promise.all([fetchPortCongestion(pol), fetchPortCongestion(pod), fetchLaneExposure(pol, pod)])
      .then(([a, b, s]) => {
        if (!live) return
        setPolCong(a)
        setPodCong(b)
        setStorms(s)
      })
      .catch(() => {})
      .finally(() => live && setLoaded(true))
    return () => {
      live = false
    }
  }, [pol, pod])

  const congChips = [
    { code: pol, name: polName, c: polCong },
    { code: pod, name: podName, c: podCong },
  ].filter((x) => x.c?.level)
  const worstStorm = storms[0]
  const disrupted = congChips.length > 0 || Boolean(worstStorm)

  // While loading, render nothing (avoids a flash); once loaded, always render an honest state.
  if (!loaded) return null

  if (!disrupted) {
    return (
      <div className="mb-5 flex items-center gap-2 text-sm text-[var(--text-muted)]">
        <span className="inline-block w-2 h-2 rounded-full bg-signal" />
        No active disruptions on this lane. {polName} and {podName} running normally.
        <span className="text-[var(--text-faint)]">Updated hourly.</span>
      </div>
    )
  }

  return (
    <div className="mb-6 rounded-xl border border-warn/40 bg-[var(--surface-0)] p-4">
      <div className="flex items-center gap-2 mb-3">
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--warn)"
          strokeWidth="2.2"
          aria-hidden="true"
        >
          <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
        </svg>
        <span className="font-display font-semibold text-[var(--text-primary)]">
          Current disruptions on this lane
        </span>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {congChips.map(({ code, name, c }) => {
          const lv = c?.level ? LEVEL[c.level] : null
          if (!lv) return null
          return (
            <Link
              key={code}
              to={`/ports/${code}`}
              className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium no-underline"
              style={{ color: lv.color, borderColor: lv.color }}
            >
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ background: lv.color }}
              />
              {/* The level only, as on the port page: wait numbers are the keyed answer (07.10). */}
              {name}: {lv.label}
            </Link>
          )
        })}
        {worstStorm && <StormChip storm={worstStorm} />}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-[var(--text-muted)]">updated hourly</div>
        <a
          href="#carriers"
          className="text-xs font-medium text-signal no-underline hover:opacity-80"
        >
          Compare the least-affected carriers ↓
        </a>
      </div>
    </div>
  )
}

function StormChip({ storm }: { storm: StormExposure }) {
  const { color } = intensityStyle(storm.level)
  const name = storm.cyclone?.name ?? 'storm'
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium"
      style={{ color, borderColor: color }}
      title={`Lane passes within ${Math.round(storm.min_dist_km)} km of ${name}. Weather-delay risk.`}
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        aria-hidden="true"
      >
        <path d="M12 2v6M12 16v6M4.9 4.9l4.2 4.2M14.9 14.9l4.2 4.2M2 12h6M16 12h6M4.9 19.1l4.2-4.2M14.9 9.1l4.2-4.2" />
      </svg>
      Storm-exposed: {name}
    </span>
  )
}
